import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
// Components
import Navdata from "./LayoutMenuData";
import withRouter from "Components/Common/withRouter";
// Redux
import { useDispatch, useSelector } from "react-redux";
import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import { getTranslations, logoutUser } from "slices/thunks";
// Language Helpers and Assets
import { useTranslation, withTranslation } from "react-i18next";
import { updateTranslationsAfterAPI } from "../i18n";
import languages, { RTL_LANGUAGES } from "common/languages";
import { getLanguageCodeForTranslation } from "helpers/languageHelper";
import getConfig from "config/config";
import whiteLogo from "../assets/images/logos/logo-white.svg";
import logoTriveLight from "../assets/images/logos/trive-logo-white2.svg";
import {
  RiCloseLine,
  RiLogoutBoxLine,
  RiMenuFill,
  RiUserLine,
} from "react-icons/ri";

const MobileHeaderMenu = (props: any) => {
  const currentLanguage: any = sessionStorage.getItem("I18N_LANGUAGE");

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState<any>(null);
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const [isMalaysiaConfig, setIsMalaysiaConfig] = useState(false);
  const [selectedLang, setSelectedLang] = useState<string>(
    currentLanguage || ""
  );

  const { t } = useTranslation();
  const dispatch = useDispatch<ThunkDispatch<any, any, AnyAction>>();
  const navigate = useNavigate();

  const translationsState = useSelector((state: any) => state.Translation);
  const { authData } = useSelector((state: any) => state.Dashboard);

  const userRole = authData?.userRoleType || "Partner";
  const navData = Navdata().props.children;

  useEffect(() => {
    const { company } = getConfig();

    if (company?.COMPANY_ID == 23) {
      setIsMalaysiaConfig(true);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowSize(window.innerWidth);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Filter menu items based on userRole
  const filteredNavData = navData.filter((item: any) => {
    return item.roles && item.roles.includes(userRole);
  });

  useEffect(() => {
    if (windowSize <= 992 && selectedLang) {
      dispatch(getTranslations(selectedLang));
    }
  }, [windowSize, selectedLang]);

  useEffect(() => {
    if (windowSize <= 992 && !currentLanguage && authData && authData.culture) {
      const languageCode = getLanguageCodeForTranslation(authData.culture);
      sessionStorage.setItem("I18N_LANGUAGE", languageCode);
      setSelectedLang(languageCode);
      dispatch(getTranslations(languageCode));
    }
  }, [authData.culture, windowSize]);

  useEffect(() => {
    if (translationsState.translations && selectedLang) {
      updateTranslationsAfterAPI(translationsState.translations, selectedLang);
    }
  }, [translationsState.translations, selectedLang]);

  const changeLanguageAction = (lang: string) => {
    sessionStorage.setItem("I18N_LANGUAGE", lang);
    setSelectedLang(lang);
    handleChangeDirection(lang);
    dispatch(getTranslations(lang));
    window.location.reload();
  };

  const handleChangeDirection = (lang: string) => {
    const isRtl = RTL_LANGUAGES.includes(lang);
    document.documentElement.setAttribute("dir", isRtl ? "rtl" : "ltr");
  };

  useEffect(() => {
    handleChangeDirection(selectedLang);
  }, [selectedLang]);

  const handleSubItemController = (item: any) => {
    setSelectedMenu(item);
  };

  const handleBackButton = () => {
    setSelectedMenu(null);
  };

  const handleLogout = () => {
    dispatch(logoutUser()).then((lang: string) => {
      const redirectLang = lang || "en";
      navigate(`/login/${redirectLang}`);
    });
    window.location.reload();
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const toggleMenu = () => {
    if (isUserMenuOpen) setIsUserMenuOpen(!isUserMenuOpen);
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleUserMenu = () => {
    if (isMenuOpen) setIsMenuOpen(!isMenuOpen);
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleMenuItemClick = () => {
    setIsMenuOpen(false);
    setIsUserMenuOpen(false);
    setSelectedMenu(null);
  };

  return (
    <aside className="sidebar mobile-header-fixed collap">
      <div className="sidebar-header">
        <Link to={"/dashboard"} className="logo">
          <img
            alt="trive-logo"
            className="img-fluid logo-image"
            src={isMalaysiaConfig ? whiteLogo : logoTriveLight}
          />
        </Link>
      </div>
      <div className="sidebar-account">
        <ul className={`mobile-menu-bar hamburger-menu-bar`}>
          <li className={`more ${isMenuOpen ? "active" : ""}`}>
            <RiMenuFill
              className={`ri-menu-fill ${isMenuOpen ? "text-primary" : "text-light"}`}
              size={28}
              onClick={toggleMenu}
            />
            <div className="menu-bar-list">
              <ul className="menu-bar-list-hamburger">
                {selectedMenu && selectedMenu.subItems ? (
                  <div>
                    <div
                      onClick={handleBackButton}
                      className="menu-bar-sub-item-back"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                      <span className="text">{t("back")}</span>
                    </div>

                    <h3 className="menu-bar-sub-item-title">
                      {selectedMenu.label}
                    </h3>

                    {selectedMenu.subItems?.map(
                      (subItem: any, subKey: number) => (
                        <li
                          className="menu-bar-list-item subMenu"
                          key={`subMenu-${subKey}@${subKey}`}
                          onClick={() => handleMenuItemClick()}
                        >
                          <Link to={subItem.link ? subItem.link : "/#"}>
                            <span className="text">{subItem.label}</span>
                          </Link>
                        </li>
                      )
                    )}
                  </div>
                ) : (
                  filteredNavData
                    .filter((item: any) => item.id !== "account")
                    .map((item: any, key: number) => (
                      <li
                        className="menu-bar-list-item"
                        key={`menu-bar-list-item-${item}@${key}`}
                        onClick={() => {
                          handleSubItemController(item);
                          !item.subItems && handleMenuItemClick();
                        }}
                      >
                        <Link to={item.link ? item.link : "/#"}>
                          <span className="text">{item.label}</span>
                        </Link>
                      </li>
                    ))
                )}
              </ul>
            </div>
          </li>
        </ul>

        <ul className={`mobile-menu-bar user-menu-bar`}>
          <li className={`more ${isUserMenuOpen ? "active" : ""}`}>
            {isUserMenuOpen ? (
              <RiCloseLine
                className="text-light"
                size={28}
                onClick={toggleUserMenu}
              />
            ) : (
              <RiUserLine
                className="text-light"
                size={28}
                onClick={toggleUserMenu}
              />
            )}
            <div className="menu-bar-list">
              <ul className="menu-bar-list-user">
                <h3 className="sidebar-sub-menu-title">{t("account")}</h3>
                <ul>
                  {(filteredNavData || [])
                    .filter((item: any) => item.id === "account")
                    .map((filteredItem: any) =>
                      filteredItem.subItems.map((subItem: any, key: any) => (
                        <li
                          className="menu-bar-list-item"
                          key={`sidebar-sub-menu-title-${subItem}@${key}`}
                          onClick={() => {
                            handleMenuItemClick();
                          }}
                        >
                          <Link to={subItem.link ? subItem.link : "/#"}>
                            <span className="text">{subItem.label}</span>
                          </Link>
                        </li>
                      ))
                    )}
                </ul>

                <h3 className="sidebar-sub-menu-title">{t("language")}</h3>
                <ul className="language-menu">
                  {languages.map((language, index) => (
                    <li
                      key={`language-menu-${language}@${index}`}
                      className={
                        language.languageCode == selectedLang
                          ? "active"
                          : "none"
                      }
                      onClick={() =>
                        changeLanguageAction(language.languageCode)
                      }
                    >
                      <span>{language.languageTitle}</span>
                    </li>
                  ))}
                </ul>

                {/* <label htmlFor="toggleButton" className="switch theme-toggle">
                  <input
                    type="checkbox"
                    name="toggleButton"
                    id="toggleButton"
                    checked={darkMode}
                    onChange={toggleDarkMode}
                  />
                  <div className="switch-button">
                    <span></span>
                  </div>
                  <span className="text">Dark Mode</span>
                </label> */}

                <hr className="d-none d-lg-block" />

                <div className="logout mt-5">
                  <div
                    className="d-flex align-items-center cursor-pointer gap-2"
                    onClick={handleLogout}
                  >
                    <RiLogoutBoxLine size={23} />
                    <span className="text">{t("logout")}</span>
                  </div>
                </div>
              </ul>
            </div>
          </li>
        </ul>
      </div>
    </aside>
  );
};

MobileHeaderMenu.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withRouter(withTranslation()(MobileHeaderMenu));
